import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import usePermissions from '../../Master_Settings/usePermissions';
import useFieldMasking from '../../Master_Settings/useFieldMasking';
import { getInitials } from '../../../utils/helpers';
import Swal from 'sweetalert2';
import './PhysicalAppointmentModule.css';
import DynamicCalendar from '../../../components/DynamicCalendar/DynamicCalendar';
import PhysicalAppointmentFilterationActions from './PhysicalAppointmentFilterationActions';
import PhysicalAppointmentDetails from './PhysicalAppointmentDetails';
import { LeadDetails as CommonLeadDetails } from '../../../components/common/LeadDetails/LeadDetails';
import Loader from '../../../components/Loader/Loader';
import EntityEdit from './EnityEdit';
import SubHeader from '../../../components/common/SubHeader/SubHeader';
import Alert from '../../../common/Alerts/Alert';
import {
    Box, Typography, Button, TextField, Chip, CircularProgress, IconButton, Tooltip, Avatar,
    Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem,
    FormControlLabel, Checkbox, Popper, Paper, Stack, OutlinedInput, Skeleton, Divider, Badge, Drawer
} from '@mui/material';
import {
    PhoneCall, Mail, MessageSquare, Mic,
    FileText, Upload, ClipboardCheck,
    Calendar, Percent, FileEdit, ExternalLink,
    Phone, MapPin, Search, User, X as LucideX
} from 'lucide-react';
import API_ENDPOINTS from '../../../config/apiEndpoints';
import PhysicalAppointmentList from './physicalappointment/list';
import PremiumModuleCards from '../../Task/Components/common/PremiumModuleCards';
import AppointmentTypeIcon from './AppointmentTypeIcon';
import Panels, { AppointmentTaskDetailPanel } from '../../../components/common/Panels/Panels';
import UniversalDetailContent from '../../../components/common/Panels/UniversalDetailContent';
import GridMainPanelLayout from '../../../components/common/Panels/GridMainPanelLayout';
import { MassOperationPanel } from '../../../components/common/MassOperation/MassOperation';

const getAppointmentTypeIcon = (row) => {
    return row?.IconClass || row?.iconClass || 'appointment';
};

const API_BASE = (process.env.REACT_APP_WCF_API_BASE_URL || '').replace(/\/$/, '');
const GET_LIST_FOR_SEARCH = `${API_BASE}${API_ENDPOINTS.APPOINTMENTS.GET_PHYSICAL_APPOINTMENT_LIST_FORSEARCH}`;
const PhysicalAppointmentModule = () => {
    const { permissions } = usePermissions('PAG10035');
    const { maskObject } = useFieldMasking('PAG10372#PAG10035');
    const crmOperationPerms = useMemo(() => {
        if (!permissions || !Array.isArray(permissions)) return null;
        return permissions.find(p => p.ModuleCode?.includes('PAG10035')) || {};
    }, [permissions]);

    // API Integration
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [userList, setUserList] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [globalStats, setGlobalStats] = useState({ total: 0, completed: 0, overdue: 0, open: 0 });
    const [filter, setFilter] = useState(['All']);
    const isFirstRender = useRef(true); // Tracks initial mount to avoid double-fetch
    const [searchTerm, setSearchTerm] = useState('');
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [selectedCustomSearch, setSelectedCustomSearch] = useState('Select Custom Search');
    const [viewMode, setViewMode] = useState('table');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 25;
    const [selectedTask, setSelectedTask] = useState(null);
    const [rowSelection, setRowSelection] = useState({});
    const [advancedFilters, setAdvancedFilters] = useState(null);
    const [customSearchId, setCustomSearchId] = useState(0);
    const [editingTask, setEditingTask] = useState(null);
    const API_BASE_AZURE = (process.env.REACT_APP_SERVICES_AZURE_BASEURL || '').replace(/\/$/, '');
    const Get_Scorecard_Details = `${API_BASE_AZURE}${API_ENDPOINTS.DASHBOARD.GET_SCORECARD_DETAILS_APPOINTMENT}`;
    const API_BASE = (process.env.REACT_APP_WCF_API_BASE_URL || '').replace(/\/$/, '');
    const API_BASE2 = (process.env.REACT_APP_SERVICES_API_BASE_URL || '').replace(/\/$/, '');
    const API_BASE3 = (process.env.REACT_APP_ASSETS_BASE_URL || '').replace(/\/$/, '');
    const Get_PhysicalAppointment_List = `${API_BASE2}${API_ENDPOINTS.APPOINTMENTS.GET_PHYSICAL_LIST}`
    const ADD_COMMENT = `${API_BASE_AZURE}${API_ENDPOINTS.TASKS.ADD_COMMENT}`;


    const getStatusLabel = (status) => {
        if (!status) return 'Open';
        const s = String(status).toLowerCase().trim();
        if (s === '1290' || s === 'open' || s === 'opening') return 'Open';
        if (s === '1291' || s === 'completed' || s === 'closed') return 'Completed';
        return s.charAt(0).toUpperCase() + s.slice(1);
    };

    const getIsCompleted = (task) => {
        const status = (task.status || '').toLowerCase().trim();
        const statusLabel = (task.statusLabel || '').toLowerCase().trim();
        const outcome = (task.outcomeName || '').toLowerCase().trim();
        return status === 'completed' || status === 'closed' || status === '1291' || status === '1' ||
            statusLabel === 'completed' || statusLabel === 'closed' ||
            outcome.includes('done') || outcome.includes('completed');
    };

    const getIsOverdue = (task) => {
        if (getIsCompleted(task)) return false;
        const isValEnd = task.endDate && !task.endDate.startsWith('1900');
        const isValStart = task.startDate && !task.startDate.startsWith('1900');
        const dueDateStr = isValEnd ? task.endDate : (isValStart ? task.startDate : null);
        const targetDate = parseCustomDate(dueDateStr);
        return targetDate && targetDate < new Date();
    };

    const getIsOpen = (task) => {
        return !getIsCompleted(task) && !getIsOverdue(task);
    };

    const getAppointmentStatusClass = (task) => {
        if (getIsCompleted(task)) return 'appointment-status-green';
        if (getIsOverdue(task)) return 'appointment-status-red';
        return 'appointment-status-blue'; // Open
    };

    const parseCustomDate = (dateStr) => {
        if (!dateStr || dateStr === "0" || dateStr === "null" || typeof dateStr !== 'string') return null;
        const cleanStr = String(dateStr).trim();

        // Handle WCF /Date(timestamp)/ format
        if (cleanStr.includes('/Date(')) {
            try {
                const timestamp = parseInt(cleanStr.replace(/\/Date\((-?\d+)\)\//, '$1'), 10);
                return new Date(timestamp);
            } catch (e) { return null; }
        }

        try {
            // Try standard parsing first
            const d = new Date(cleanStr.replace(' ', 'T'));
            if (!isNaN(d.getTime())) return d;

            // Custom parsing for DD-MMM-YYYY HH:mm:ss format
            const months = { 'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5, 'Jul': 6, 'Aug': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11 };
            const parts = cleanStr.split(' ');
            if (parts.length === 0) return null;

            const dateParts = parts[0].split('-');
            if (dateParts.length < 3) return null;

            const day = parseInt(dateParts[0], 10);
            const monthStr = dateParts[1];
            const month = isNaN(monthStr) ? months[monthStr] : parseInt(monthStr, 10) - 1;
            const yearStr = dateParts[2];
            const year = yearStr.length === 2 ? 2000 + parseInt(yearStr, 10) : parseInt(yearStr, 10);

            let hours = 0, minutes = 0, seconds = 0;
            if (parts[1]) {
                const timeParts = parts[1].split(':');
                hours = parseInt(timeParts[0], 10) || 0;
                minutes = parseInt(timeParts[1], 10) || 0;
                seconds = parseInt(timeParts[2], 10) || 0;
                if (parts[2] === 'PM' && hours < 12) hours += 12;
                if (parts[2] === 'AM' && hours === 12) hours = 0;
            }
            const finalDate = new Date(year, month, day, hours, minutes, seconds);
            return isNaN(finalDate.getTime()) ? null : finalDate;
        } catch (e) {
            console.error("Error parsing date:", dateStr, e);
            return null;
        }
    };


    const isOverdue = (task) => getIsOverdue(task);

    const getTaskCompletionBadge = (task) => {
        const isCompleted = getIsCompleted(task);

        const isValEnd = task.endDate && !task.endDate.startsWith('1900');
        const isValStart = task.startDate && !task.startDate.startsWith('1900');
        const dueDateStr = isValEnd ? task.endDate : (isValStart ? task.startDate : null);
        const completedDateStr = task.CompletedOn || task.completedDate || task.ModifiedOn || task.CreatedOn;

        if (!dueDateStr) return null;

        const now = new Date();
        now.setHours(0, 0, 0, 0);
        const dDate = parseCustomDate(dueDateStr);
        if (!dDate) return null;

        const isOverdueFlag = getIsOverdue(task);

        // Completed Logic
        if (isCompleted) {
            if (!completedDateStr) return <span className="status-badge-inline completed" style={{ textTransform: 'none' }}>completed</span>;
            const compDate = parseCustomDate(completedDateStr);
            if (!compDate) return <span className="status-badge-inline completed" style={{ textTransform: 'none' }}>completed</span>;

            const d1 = new Date(dDate); d1.setHours(0, 0, 0, 0);
            const d2 = new Date(compDate); d2.setHours(0, 0, 0, 0);
            const diffDays = Math.floor((d2.getTime() - d1.getTime()) / (1000 * 60 * 60 * 24));

            if (diffDays === 0) return <span className="status-badge-inline completed">completed on same day</span>;
            if (diffDays < 0) return <span className="status-badge-inline completed">completed {Math.abs(diffDays)} days before due date</span>;
            return <span className="status-badge-inline completed">completed after {diffDays} days</span>;
        }

        // Overdue Logic
        if (isOverdueFlag) {
            const d1 = new Date(dDate); d1.setHours(0, 0, 0, 0);
            const diffDays = Math.floor((now.getTime() - d1.getTime()) / (1000 * 60 * 60 * 24));
            if (diffDays > 0) return <span className="status-badge-inline overdue" style={{ textTransform: 'none' }}>overdue {diffDays} days</span>;
            return <span className="status-badge-inline overdue">OVERDUE</span>;
        }

        // Open Logic
        const d1 = new Date(dDate); d1.setHours(0, 0, 0, 0);
        const diffDays = Math.floor((d1.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        if (diffDays === 0) return <span className="status-badge-inline due-today">due today</span>;
        return <span className="status-badge-inline open">due in {diffDays} days</span>;
    };

    const [viewingDetailTask, setViewingDetailTask] = useState(null);
    const [activeMeatball, setActiveMeatball] = useState(null);
    const [activeSettingsMenu, setActiveSettingsMenu] = useState(null);
    const [detailMode, setDetailMode] = useState('view'); // 'view', 'followup', 'add-deal'
    const [viewingLeadDetail, setViewingLeadDetail] = useState(null); // stores Lead ID
    const [isCustomDownloadOpen, setIsCustomDownloadOpen] = useState(false);
    const [customExportFields, setCustomExportFields] = useState([]);
    const [customSettings, setCustomSettings] = useState([]);
    const [selectedExportFields, setSelectedExportFields] = useState([]);
    const [selectedCustomSetting, setSelectedCustomSetting] = useState('');
    const [exportSettingName, setExportSettingName] = useState('');
    const [isDownloading, setIsDownloading] = useState(false);
    const [fieldSearchTerm, setFieldSearchTerm] = useState('');
    const [taskToRemove, setTaskToRemove] = useState(null);
    const [showMarkCompleteModal, setShowMarkCompleteModal] = useState(false);
    const [markCompleteTask, setMarkCompleteTask] = useState(null);
    const [markCompletePayload, setMarkCompletePayload] = useState({ OutcomesId: '', Remarks: '' });

    const [alertConfig, setAlertConfig] = useState({
        show: false,
        type: 'success',
        title: '',
        message: '',
        onConfirm: () => { },
        onCancel: () => setAlertConfig(prev => ({ ...prev, show: false }))
    });

    const [showCommentModal, setShowCommentModal] = useState(false);
    const [commentTask, setCommentTask] = useState(null);
    const [commentData, setCommentData] = useState({
        remarks: '',
        attachmentTitle: '',
        attachmentFile: null,
        attachmentName: ''
    });



    const handleCreateAction = (actionLabel) => {
        // Map labels to internal modes or flags
        const modeMap = {
            'Add Followup': 'followup',
            'Send Mail': 'mail',
            'Send Voice': 'voice',
            'Upload Document': 'upload',
            'Add Task': 'task',
            'Add Appointment': 'appointment',
            'Add Deal': 'deal',
            'Fill Webform': 'webform'
        };

        const actionType = modeMap[actionLabel] || 'edit';

        // Trigger Sidebar in Edit mode with a "New" flag
        setViewingDetailTask({
            isNew: true,
            taskTitle: '',
            description: '',
            actionType: actionType,
            startDate: new Date().toISOString(),
            endDate: new Date().toISOString(),
            status: 'Open'
        });
        setDetailMode(actionType);
    };

    const urlRemove = `${API_BASE2}${API_ENDPOINTS.APPOINTMENTS.PHYSICAL_APPOINTMENT_REMOVE}`;
    const urlMarkComplete = `${API_BASE2}/EmployeeTracker/PhysicalAppointmentOutcomeUpdate`;


    const urlExport = `${API_BASE3}${API_ENDPOINTS.APPOINTMENTS.PHYSICAL_APPOINTMENT_REMOVE}`;
    const fetchStats = async () => {
        try {
            const userId = localStorage.getItem('userId') || '34594';
            const response = await fetch(Get_Scorecard_Details, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userid: userId })
            });
            const result = await response.json();
            if (result && result.Details) {
                const d = result.Details;
                setGlobalStats({
                    total: d.Total || 0,
                    completed: d.Completed || 0,
                    overdue: d.Overdue || 0,
                    open: (d.Total || 0) - (d.Completed || 0)
                });
            }
        } catch (error) {
            console.error("Error fetching appointment stats:", error);
        }
    };

    const handleRemove = async (task = null) => {
        const targetTask = task || taskToRemove;
        if (!targetTask) return;
        setLoading(true);
        try {
            const payload = {
                Details: {
                    Mode: "PD",
                    PhysicalAppointmentId: String((targetTask.PhysicalAppointmentId || targetTask.id || '').toString().split('_')[0]),
                    CreatedBy: String(targetTask.createdBy || targetTask.CreatedBy || localStorage.getItem('userId') || "34594")
                },
                LoggedUserId: String(localStorage.getItem('userId') || "34594"),
                Token: localStorage.getItem('token') || "-2295521862261168"
            };
            const response = await fetch(urlRemove, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const resData = await response.json();
            if (response.ok && (resData.Status === 1 || resData.Status === 200 || (resData.Message && resData.Message.toLowerCase().includes("success")))) {
                setAlertConfig({
                    show: true,
                    type: 'success',
                    title: 'Success',
                    message: 'Appointment removed successfully.',
                    onConfirm: () => setAlertConfig(prev => ({ ...prev, show: false }))
                });
                // Refresh list
                initializeAppointments();
                fetchAppointments();
            } else {
                setAlertConfig({
                    show: true,
                    type: 'error',
                    title: 'Error',
                    message: resData.Message || "Failed to remove appointment.",
                    onConfirm: () => setAlertConfig(prev => ({ ...prev, show: false }))
                });
            }
        } catch (e) {
            console.error(e);
            setAlertConfig({
                show: true,
                type: 'error',
                title: 'Error',
                message: "An error occurred while removing.",
                onConfirm: () => setAlertConfig(prev => ({ ...prev, show: false }))
            });
        } finally {
            setLoading(false);
            setTaskToRemove(null);
        }
    };

    const submitMarkComplete = async () => {
        if (!markCompletePayload.OutcomesId || !markCompletePayload.Remarks.trim()) {
            setAlertConfig({
                show: true,
                type: 'error',
                title: 'Error',
                message: 'Please select an outcome and enter remarks.',
                onConfirm: () => setAlertConfig(prev => ({ ...prev, show: false }))
            });
            return;
        }
        setLoading(true);
        try {
            const now = new Date();
            const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
            const formattedDate = `${String(now.getDate()).padStart(2, '0')}-${months[now.getMonth()]}-${now.getFullYear()} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

            const payload = {
                Token: "-2295521862261168",
                LoggedUserId: String(localStorage.getItem('userId') || "34594"),
                Details: {
                    Mode: "PO",
                    PhysicalAppointmentId: String((markCompleteTask.PhysicalAppointmentId || markCompleteTask.id || '').toString().split('_')[0]),
                    Remarks: markCompletePayload.Remarks,
                    OutcomesId: markCompletePayload.OutcomesId,
                    CompletedOn: formattedDate,
                    CreatedBy: String(localStorage.getItem('userId') || "34594")
                }
            };

            const response = await fetch(urlMarkComplete, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const result = await response.json();

            if (response.ok && (result.Status === 1 || (result.Message && result.Message.toLowerCase().includes("success")))) {
                setShowMarkCompleteModal(false);
                setAlertConfig({
                    show: true,
                    type: 'success',
                    title: 'Success',
                    message: 'Outcome Saved Successfully.',
                    onConfirm: () => setAlertConfig(prev => ({ ...prev, show: false }))
                });
                initializeAppointments();
                fetchAppointments();
            } else {
                setAlertConfig({
                    show: true,
                    type: 'error',
                    title: 'Error',
                    message: result.Message || "Failed to save outcome.",
                    onConfirm: () => setAlertConfig(prev => ({ ...prev, show: false }))
                });
            }
        } catch (e) {
            console.error(e);
            setAlertConfig({
                show: true,
                type: 'error',
                title: 'Error',
                message: "An error occurred while saving outcome.",
                onConfirm: () => setAlertConfig(prev => ({ ...prev, show: false }))
            });
        } finally {
            setLoading(false);
        }
    };

    const handleAddCommentClick = (task) => {
        setCommentTask(task);
        setCommentData({ remarks: '', attachmentTitle: '', attachmentFile: null, attachmentName: '' });
        setShowCommentModal(true);
    };

    const submitAddComment = async () => {
        if (!commentData.remarks.trim()) {
            setAlertConfig({
                show: true,
                type: 'error',
                title: 'Error',
                message: 'Please enter a comment.',
                onConfirm: () => setAlertConfig(prev => ({ ...prev, show: false }))
            });
            return;
        }

        setLoading(true);
        try {
            const taskIdInt = parseInt((commentTask.PhysicalAppointmentId || commentTask.id || '0').toString().split('_')[0], 10);
            const userIdInt = parseInt(localStorage.getItem("userId") || "34594", 10);

            const details = {
                Token: "-2295521862261168",
                taskAttachment: {
                    Remarks: commentData.remarks,
                    CreatedBy: userIdInt,
                    TaskId: taskIdInt,
                    ParentId: userIdInt,
                    Mode: "C",
                    AttachmentTitle: commentData.attachmentTitle || ""
                }
            };

            const formData = new FormData();
            formData.append('details', JSON.stringify(details));
            if (commentData.attachmentFile) {
                formData.append('file', commentData.attachmentFile);
            }

            const response = await fetch(ADD_COMMENT, {
                method: 'POST',
                headers: {
                    "X-Requested-With": "XMLHttpRequest"
                },
                body: formData
            });

            if (response.ok) {
                const result = await response.json();
                if (result.Status === 1 || result.Message === "Success" || result.d === "Success") {
                    setShowCommentModal(false);
                    setAlertConfig({
                        show: true,
                        type: 'success',
                        title: 'Success',
                        message: 'Comment added successfully.',
                        onConfirm: () => setAlertConfig(prev => ({ ...prev, show: false }))
                    });
                    fetchAppointments();
                } else {
                    setAlertConfig({
                        show: true,
                        type: 'error',
                        title: 'Error',
                        message: result.Message || "Failed to add comment.",
                        onConfirm: () => setAlertConfig(prev => ({ ...prev, show: false }))
                    });
                }
            } else {
                setAlertConfig({
                    show: true,
                    type: 'error',
                    title: 'Error',
                    message: `Failed to add comment. Server returned ${response.status}`,
                    onConfirm: () => setAlertConfig(prev => ({ ...prev, show: false }))
                });
            }
        } catch (e) {
            console.error("Error adding comment:", e);
            setAlertConfig({
                show: true,
                type: 'error',
                title: 'Error',
                message: "An error occurred while adding the comment.",
                onConfirm: () => setAlertConfig(prev => ({ ...prev, show: false }))
            });
        } finally {
            setLoading(false);
        }
    };

    // Fetch Custom Export data when modal opens
    useEffect(() => {
        if (isCustomDownloadOpen) {
            const fetchCustomExportData = async () => {
                const uid = localStorage.getItem('userId') || '34594';
                const token = API_ENDPOINTS.TOKE.UNIVERSAL_TOKEN.toString();

                // 1. Fetch Placeholders
        try {
          const NEWV3_BASE_URL = (process.env.REACT_APP_SERVICES_API_BASE_URL || '').replace(/\/$/, '');
          const phUrl = `${NEWV3_BASE_URL}/UserCRM/FuncToGetEntityPlaceHolder`;
          const res = await fetch(phUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ Token: token, Details: { strEntityName: "Lead", strParentID: uid } })
          });
          const result = await res.json();
          if (result && result.Status === 1 && result.Details) {
            setCustomExportFields(result.Details);
          } else if (result && result.d) {
            setCustomExportFields(result.d);
          }
        } catch (e) { console.error('Error fetching placeholders:', e); }

                // 2. Fetch Settings
                try {
                    const settingsUrl = `${API_BASE_AZURE}${API_ENDPOINTS.COMMON.EXPORT_DATA_SETTING}`;
                    const payload = {
                        Details: {
                            Id: 0,
                            SettingName: "",
                            PageUrl: "/UserCRMCampaign/ToDo/physicalappointment.aspx",
                            CsvColumns: "",
                            Section: "PhysicalAppointment",
                            EntityName: "Entity",
                            UserId: uid,
                            Mode: "get"
                        },
                        Token: token
                    };
                    const res = await fetch(settingsUrl, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(payload)
                    });
                    const result = await res.json();
                    if (result && result.Details && result.Details.data) {
                        setCustomSettings(result.Details.data);
                    } else if (result && result.data) {
                        setCustomSettings(result.data);
                    }
                } catch (e) { console.error('Error fetching settings:', e); }
            };
            fetchCustomExportData();
        }
    }, [isCustomDownloadOpen]);

    const handleSaveExportSetting = async () => {
        let nameToSave = exportSettingName;
        let existingId = 0;

        if (!nameToSave && selectedCustomSetting) {
            const currentSetting = customSettings.find(s => s.Id.toString() === selectedCustomSetting);
            if (currentSetting) {
                nameToSave = currentSetting.SettingName;
                existingId = currentSetting.Id;
            }
        }

        if (!nameToSave || selectedExportFields.length === 0) {
            alert("Please enter a name for the setting and select at least one field.");
            return;
        }

        const uid = localStorage.getItem('userId') || '34594';
        const token = API_ENDPOINTS.TOKE.UNIVERSAL_TOKEN.toString();
        try {
            const payload = {
                Details: {
                    Id: existingId,
                    SettingName: nameToSave,
                    PageUrl: "/UserCRMCampaign/ToDo/physicalappointment.aspx",
                    CsvColumns: selectedExportFields.join(','),
                    Section: "PhysicalAppointment",
                    EntityName: "Entity",
                    UserId: uid,
                    Mode: "save"
                },
                Token: token
            };
            const settingsUrl = `${API_BASE_AZURE}${API_ENDPOINTS.COMMON.EXPORT_DATA_SETTING}`;
            const res = await fetch(settingsUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const result = await res.json();
            if (result.Status === 1 || result.Message === "Success") {
                alert("Setting saved successfully!");
                setExportSettingName('');
                const fetchSettingsAfterSave = async () => {
                    const sPayload = {
                        Details: {
                            Id: 0, SettingName: "", PageUrl: "/UserCRMCampaign/ToDo/physicalappointment.aspx",
                            CsvColumns: "", Section: "PhysicalAppointment", EntityName: "Entity", UserId: uid, Mode: "get"
                        },
                        Token: token
                    };
                    const sRes = await fetch(settingsUrl, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(sPayload)
                    });
                    const sResult = await sRes.json();
                    if (sResult && sResult.Details && sResult.Details.data) {
                        setCustomSettings(sResult.Details.data);
                    } else if (sResult && sResult.data) {
                        setCustomSettings(sResult.data);
                    }
                };
                fetchSettingsAfterSave();
            } else {

                alert("Failed to save: " + result.Message);
            }
        } catch (e) {
            console.error('Error saving setting:', e);
            alert("Error saving setting. Check console.");
        }
    };

    const handleCustomDownloadSubmit = async () => {
        if (selectedExportFields.length === 0) {
            alert("Select fields first.");
            return;
        }
        setIsDownloading(true);
        const uid = localStorage.getItem('userId') || '34594';
        try {
            const payload = {
                UserId: uid,
                EntityName: "Lead",
                PageMode: "PhysicalAppointment",
                Mode: "P",
                CriteriaName: searchTerm || "",
                FilterText: "",
                csvColumnList: selectedExportFields.join(','),
                PageSection: "PhysicalAppointment",
                FollowUpType: "AssignedTo",
                AssignedTo: "0",
                CreatedBy: "",
                NextStatusFromDate: "",
                NextStatusToDate: "",
                CreatedFromDate: "",
                CreatedToDate: "",
                LeadId: "0",
                Remarks: "",
                Overdue: "false",
                DueToday: "false",
                Scheduled: "false",
                NoFollowUp: "false",
                IsReAssign: "false"
            };

            const form = document.createElement('form');
            form.method = 'POST';
            form.action = 'https://wcfkit19.azurewebsites.net/UserCRMCampaign/Service/CallReportService.asmx/ExportWidgetDownload';
            form.target = '_blank';

            Object.keys(payload).forEach(key => {
                const input = document.createElement('input');
                input.type = 'hidden';
                input.name = key;
                input.value = payload[key];
                form.appendChild(input);
            });

            document.body.appendChild(form);
            form.submit();
            document.body.removeChild(form);

        } catch (e) {
            console.error('Download error:', e);
            alert("An error occurred during download.");
        }
        finally { setIsDownloading(false); }
    };


    const handleExportPhysicalAppointment = () => {
        const dataToExport = filteredTasks || tasks || [];
        if (dataToExport.length === 0) {
            Swal.fire('Warning', 'No physical appointments to export.', 'warning');
            return;
        }

        Swal.fire({
            title: 'Generating Export...',
            text: 'Please wait while we prepare your data',
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });

        // Helper for specific Excel date format: M/D/YYYY H:MM:SS AM/PM
        const formatExcelDate = (dateStr) => {
            if (!dateStr || dateStr === '0' || dateStr === 'null') return '';
            const d = parseCustomDate(dateStr);
            if (!d || isNaN(d.getTime())) return dateStr;

            const m = d.getMonth() + 1;
            const day = d.getDate();
            const y = d.getFullYear();

            let h = d.getHours();
            const min = String(d.getMinutes()).padStart(2, '0');
            const sec = String(d.getSeconds()).padStart(2, '0');
            const ampm = h >= 12 ? 'PM' : 'AM';

            h = h % 12;
            h = h ? h : 12;

            return `${m}/${day}/${y} ${h}:${min}:${sec} ${ampm}`;
        };

        // CSV Header - matching the user's image exactly
        const headers = [
            "Title",
            "Description",
            "Address",
            "StartDate",
            "EndDate",
            "STATUS",
            "AssignedTo",
            "CreatorName",
            "CompletedDate",
            "Remarks",
            "Collaborators",
            "OwnerName",
            "OutcomeName",
            "TaskStatus",
            "Threshold"
        ];

        // Map data to rows
        const rows = dataToExport.map(t => {
            const maskedT = maskObject({ ...t });
            return [
                maskedT.taskTitle || '',
                maskedT.description || '',
                maskedT.FullAddress || maskedT.Address || maskedT.address || '',
                formatExcelDate(maskedT.startDate),
                formatExcelDate(maskedT.endDate),
                maskedT.status || '',
                maskedT.assignedTo || '',
                maskedT.creatorName || '',
                formatExcelDate(maskedT.CompletedDate || maskedT.completedDate),
                maskedT.remarks || '',
                maskedT.collaboratorList || '',
                maskedT.OwnerName || maskedT.ownerName || '',
                maskedT.outcomeName || '',
                maskedT.taskStatus || '',
                maskedT.Threshold || maskedT.threshold || '0'
            ];
        });

        // Helper to escape CSV values and add spacing for Excel
        const escapeCSV = (val) => {
            if (val === null || val === undefined) return '""';
            let str = String(val).replace(/"/g, '""');
            // Adding a leading space (" " + str) provides visual spacing in Excel 
            // and prevents auto-formatting of large numbers/dates
            return `" ${str}"`;
        };

        // Create CSV string
        let csvContent = headers.map(h => `"${h}"`).join(",") + "\n";
        rows.forEach(row => {
            csvContent += row.map(escapeCSV).join(",") + "\n";
        });

        // Trigger Download
        try {
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            const dateStr = new Date().toISOString().split('T')[0];

            link.setAttribute("href", url);
            link.setAttribute("download", `Physical_Appointments_${dateStr}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            Swal.fire({
                icon: 'success',
                title: 'Export Successful',
                text: `${dataToExport.length} appointments exported successfully!`,
                timer: 2000,
                showConfirmButton: false
            });
        } catch (err) {
            console.error("Export error:", err);
            Swal.fire('Error', 'Failed to generate export file.', 'error');
        }
    };

    const handleExportTasks = () => {
        handleExportPhysicalAppointment();
    };

    // Helper: builds the exact same payload as the main fetchAppointments API
    const buildAppointmentPayload = (filterQuery, searchText, userId, customSearch, pagingSize = 2000, offsetValue = 0) => {
        return JSON.stringify({
            Token: "-2295521862261168",
            Details: {
                UserId: parseInt(userId, 10) || 34594,
                FilterText: filterQuery || "  ",
                AType: "",
                uiId: "",
                FromDate: "",
                EndDate: "",
                CriteriaName: customSearch || "",
                OffsetValue: offsetValue,
                PagingSize: pagingSize
            }
        });
    };

    // On page load: fire 4 parallel API calls with EXACT same payload as filter clicks
    // Uses recordsTotal from each to populate counter badges
    const initializeAppointments = useCallback(async () => {
        const userId = localStorage.getItem('userId') || '34594';
        setLoading(true);
        try {
            const filterConfigs = [
                { key: 'total', filterText: '' },
                { key: 'open', filterText: "AND ( (Status = 'open') ) And ( ( 1 = 1 ) ) And ( 1 = 1 )" },
                { key: 'completed', filterText: "AND ( (Status = 'completed') ) And ( ( 1 = 1 ) ) And ( 1 = 1 )" },
                { key: 'overdue', filterText: "AND ( (Status = 'Over Due') ) And ( ( 1 = 1 ) ) And ( 1 = 1 )" },
            ];

            const requests = filterConfigs.map(cfg => {
                const pSize = cfg.key === 'total' ? itemsPerPage : 25;
                const payload = buildAppointmentPayload(cfg.filterText, '', userId, '', pSize, 0);
                return fetch(Get_PhysicalAppointment_List, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json, text/javascript, */*; q=0.01'
                    },
                    body: payload
                }).then(r => r.json()).then(result => ({ key: cfg.key, result }));
            });

            const responses = await Promise.all(requests);

            const newStats = { total: 0, open: 0, completed: 0, overdue: 0 };
            responses.forEach(({ key, result }) => {
                let dataArray = result?.Details?.data || result?.Details || result?.data || result;
                if (typeof dataArray === 'string') {
                    try { dataArray = JSON.parse(dataArray); } catch(e) { dataArray = []; }
                }
                if (dataArray && dataArray.data && Array.isArray(dataArray.data)) {
                    dataArray = dataArray.data;
                }
                if (!Array.isArray(dataArray)) dataArray = [];

                const totalRows = dataArray.length > 0 ? (dataArray[0].TotalRow || dataArray.length) : (result?.recordsTotal || 0);

                newStats[key] = totalRows;

                // Use the 'total' (no filter) response to populate the main list
                if (key === 'total') {
                    const mappedTasks = mapAppointmentData(dataArray);
                    setTasks(mappedTasks);
                    if (mappedTasks.length > 0) setSelectedTask(mappedTasks[0]);
                    setTotalRecords(totalRows);
                }
            });
            setGlobalStats(newStats);
        } catch (err) {
            console.error('Error initializing appointments:', err);
        } finally {
            setLoading(false);
            setIsInitialLoad(false);
        }
    }, [Get_PhysicalAppointment_List]);


    // Shared mapper for API data items → internal task objects
    const mapAppointmentData = (data) => {
        return data.map((item, idx) => {
            let bestName = "-";
            if (item.PersonName && String(item.PersonName).trim() !== '' && String(item.PersonName).toLowerCase() !== 'unknown') {
                bestName = item.PersonName;
            } else if (item.personName && String(item.personName).trim() !== '' && String(item.personName).toLowerCase() !== 'unknown') {
                bestName = item.personName;
            } else if (item.GeoEntityName && String(item.GeoEntityName).trim() !== '' && String(item.GeoEntityName).toLowerCase() !== 'unknown') {
                bestName = item.GeoEntityName;
            } else if (item.EntityName && String(item.EntityName).trim() !== '' && String(item.EntityName).toLowerCase() !== 'unknown') {
                bestName = item.EntityName;
            } else if (item.goEntityName && String(item.goEntityName).trim() !== '' && String(item.goEntityName).toLowerCase() !== 'unknown') {
                bestName = item.goEntityName;
            } else if (item.Name && String(item.Name).trim() !== '' && String(item.Name).toLowerCase() !== 'unknown') {
                bestName = item.Name;
            }
            const dateVal = item.EndDate || item.StartDate || item.CreatedOn || '';
            const parsedDate = parseCustomDate(dateVal);
            const startDateParsed = parseCustomDate(item.StartDate);
            const endDateParsed = parseCustomDate(item.EndDate);
            const baseItem = {
                ...item,
                id: String(item.PhysicalAppointmentId || item.PhysicalappointmentId || item.AppointmentId || 'appt') + '_' + idx,
                name: bestName,
                taskTitle: item.Title,
                phone: String(item.Mobile || item.MobileNo || item.Phone || '').replace(/,+$/, '').trim(),
                email: String(item.EmailId || item.Email || '').replace(/,+$/, '').trim(),
                EntityId: item.EntityId || item.LeadId || item.EnquiryId || '',
                enquiryId: item.EntityId ? item.EntityId.toString() : (item.LeadId ? item.LeadId.toString() : (item.EnquiryId ? item.EnquiryId.toString() : '')),
                status: item.Status || 'Open',
                date: dateVal,
                _parsedDate: parsedDate,
                _startDateParsed: startDateParsed,
                _endDateParsed: endDateParsed,
                type: (item.goScoEntityType || item.GeoEntityType || (item.LeadId ? 'lead' : (item.EnquiryId ? 'enquiry' : ''))).toLowerCase().includes('enquiry') ? 'Mic' : 'Lead',
                createdDate: item.CreatedOn || item.CreatedDate || '',
                description: item.Description || '',
                startDate: item.StartDate || '',
                endDate: item.EndDate || '',
                creatorName: item.CreatorName || item.OwnerName || '',
                assignedTo: item.AssignedTo || '',
                taskStatus: item.TaskStatus || item.Status || 'Open',
                outcomeName: item.OutcomeName || '',
                remarks: item.Remarks || '',
                collaboratorList: item.CollaboratorList || item.CollaboratorNames || item.CollaboratorUserIds || item.Collaborators || item.Collaboraters || item.CollabratoerUserIds || '',
                statusLabel: getStatusLabel(item.Status || item.TaskStatus || '1290'),
                address: item.Address || '',
                taskName: item.TasKName || item.TaskName || '',
                iconClass: item.IconClass || ''
            };
            baseItem._isCompleted = getIsCompleted(baseItem);
            baseItem._isOverdue = getIsOverdue(baseItem);
            baseItem._isOpen = !baseItem._isCompleted && !baseItem._isOverdue;
            return baseItem;
        });
    };

    const fetchAppointments = async () => {
        try {
            setLoading(true);
            const userId = localStorage.getItem('userId') || '34594';

            const activeFilters = (filter || []).map(f => f.toLowerCase());
            const isAll = activeFilters.includes('all');

            let filterText = '';
            if (!isAll) {
                let statusQuery = '';
                if (activeFilters.includes('overdue')) statusQuery = "Status = 'Over Due'";
                else if (activeFilters.includes('completed')) statusQuery = "Status = 'completed'";
                else if (activeFilters.includes('open') || activeFilters.includes('scheduled')) statusQuery = "Status = 'open'";
                if (statusQuery) {
                    filterText = `AND ( (${statusQuery}) ) And ( ( 1 = 1 ) ) And ( 1 = 1 )`;
                }
            }

            const offset = parseInt((currentPage - 1) * itemsPerPage);
            const payload = buildAppointmentPayload(filterText, '', userId, selectedCustomSearch, itemsPerPage, offset);
            const isSearchActive = selectedCustomSearch && selectedCustomSearch !== 'Select Custom Search';
            const url1 = isSearchActive ? GET_LIST_FOR_SEARCH : Get_PhysicalAppointment_List;

            const response = await fetch(url1, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json, text/javascript, */*; q=0.01'
                },
                body: payload
            });

            if (!response.ok) throw new Error(`Server responded with ${response.status}`);
            const result = await response.json();

            if (result) {
                let dataArray = result?.Details?.data || result?.Details || result?.data || result;
                if (typeof dataArray === 'string') {
                    try { dataArray = JSON.parse(dataArray); } catch(e) { dataArray = []; }
                }
                if (dataArray && dataArray.data && Array.isArray(dataArray.data)) {
                    dataArray = dataArray.data;
                }
                if (!Array.isArray(dataArray)) dataArray = [];

                const mappedTasks = mapAppointmentData(dataArray);
                setTasks(mappedTasks);
                if (mappedTasks.length > 0) setSelectedTask(mappedTasks[0]);
                const totalRows = dataArray.length > 0 ? (dataArray[0].TotalRow || dataArray.length) : (result?.recordsTotal || 0);
                setTotalRecords(totalRows);

                // We skip updating stats state directly here to avoid "jumping"
                // The stats useMemo will handle it based on totalRecords during search
            }
        } catch (error) {
            console.error("Error fetching physical appointments:", error);
        } finally {
            setLoading(false);
            setIsInitialLoad(false);
        }

    };

    const fetchUserList = async () => {
        try {
            const url = `${API_BASE}/UserCRMCampaign/Service/ToDoService.asmx/GetUserListByUser_Id`;
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json; charset=utf-8' },
                body: JSON.stringify({ userid: localStorage.getItem('userId') || "34594" })
            });
            const result = await response.json();
            if (result.d) {
                const parsed = typeof result.d === 'string' ? JSON.parse(result.d) : result.d;
                const mapped = Array.isArray(parsed) ? parsed.map(u => ({ UserId: String(u.Code), UserName: u.Text })) : [];
                setUserList(mapped);
            }
        } catch (e) { console.error("Error fetching user list for export mapping:", e); }
    };

    const hasInitializedRef = useRef(false);
    const prevFiltersRef = useRef({ filter, selectedCustomSearch, currentPage });

    useEffect(() => {
        if (!hasInitializedRef.current) {
            hasInitializedRef.current = true;
            fetchUserList();
            initializeAppointments(); // Fire 4 parallel APIs on load for all counters + main list
        }
    }, [initializeAppointments]); // Only run once on true mount

    useEffect(() => {
        const prev = prevFiltersRef.current;
        if (prev.filter === filter && 
            prev.selectedCustomSearch === selectedCustomSearch && 
            prev.currentPage === currentPage) {
            return; // Skip initial mount and strict-mode remounts unless dependencies actually changed
        }
        prevFiltersRef.current = { filter, selectedCustomSearch, currentPage };
        fetchAppointments();
    }, [filter, selectedCustomSearch, currentPage]);





    const handleCustomSearchSubmit = (criteria) => {
        const criteriaName = criteria.CriteriaName || criteria.Text || criteria.Name || 'Select Custom Search';
        setSelectedCustomSearch(criteriaName);
        setCurrentPage(1);
    };

    const handleCustomSearchClear = () => {
        setSelectedCustomSearch('Select Custom Search');
        setCurrentPage(1);
    };


    const appointmentFields = [
        { label: 'Appointment Title', value: 'taskTitle' },
        { label: 'Lead Name', value: 'name' },
        { label: 'Enquiry ID', value: 'enquiryId' },
        { label: 'Description', value: 'description' },
        { label: 'Address', value: 'Address' },
        { label: 'EndDate', value: 'endDate' },
        { label: 'Status', value: 'statusLabel' },
        { label: 'Outcome', value: 'outcomeName' }
    ];

    const handleFilterChange = (newFilters) => {
        setAdvancedFilters(newFilters);
        setCurrentPage(1);
    };

    useEffect(() => {
        if (currentPage !== 1) {
            setCurrentPage(1);
        }
    }, [searchTerm]);

    const hasMassPerms = crmOperationPerms && (
        crmOperationPerms.MassUpdate || crmOperationPerms.MassDelete || 
        crmOperationPerms.MassSMS || crmOperationPerms.MassEmail || 
        crmOperationPerms.MassAssign || crmOperationPerms.MassBroadcast
    );

    const columns = useMemo(() => {
        const cols = [
            { id: 'select', label: '', type: 'checkbox' },
            { id: 'taskTitle', label: 'Title' },
            { id: 'name', label: 'Name' },
            { id: 'date', label: 'Date' },
            { id: 'status', label: 'Status' },
            { id: 'actions', label: 'Actions' }
        ];
        return hasMassPerms ? cols : cols.filter(c => c.id !== 'select');
    }, [crmOperationPerms, hasMassPerms]);

    const stats = useMemo(() => {
        const counts = {
            total: searchTerm ? totalRecords : (globalStats.total || 0),
            overdue: globalStats.overdue || 0,
            open: globalStats.open || 0,
            completed: globalStats.completed || 0
        };

        // Only reflect filtered totalRecords in status cards if a search is active
        if (searchTerm) {
            const activeFilter = (filter[0] || 'All').toLowerCase();
            if (activeFilter === 'overdue') counts.overdue = totalRecords;
            else if (activeFilter === 'completed') counts.completed = totalRecords;
            else if (activeFilter === 'open' || activeFilter === 'scheduled') counts.open = totalRecords;
            else if (activeFilter === 'all') counts.total = totalRecords;
        }

        return counts;
    }, [totalRecords, globalStats, filter, searchTerm]);

    const filteredTasks = useMemo(() => {
        let result = tasks;

        // 1. Search Filter (Phrase-based exact matching to avoid over-greedy matches)
        if (searchTerm) {
            const query = searchTerm.toLowerCase().trim();
            if (query) {
                result = result.filter(t => {
                    return (
                        (t.name || '').toLowerCase().includes(query) ||
                        (t.PersonName || '').toLowerCase().includes(query) ||
                        (t.EntityName || '').toLowerCase().includes(query) ||
                        (t.GeoEntityName || '').toLowerCase().includes(query) ||
                        (t.taskTitle || '').toLowerCase().includes(query) ||
                        (t.Title || '').toLowerCase().includes(query) ||
                        (t.title || '').toLowerCase().includes(query) ||
                        (t.description || '').toLowerCase().includes(query) ||
                        (t.phone || '').toLowerCase().includes(query) ||
                        (t.MobileNo || '').toLowerCase().includes(query) ||
                        (t.Phone || '').toLowerCase().includes(query) ||
                        (t.creatorName || '').toLowerCase().includes(query) ||
                        (t.OwnerName || '').toLowerCase().includes(query)
                    );
                });
            }
        }

        /* Server-side status filtering is now active
        if (filter && !filter.includes('All')) {
            const activeFilters = filter.map(f => f.toLowerCase());
            result = result.filter(task => {
                if (activeFilters.includes('overdue') && task._isOverdue) return true;
                if (activeFilters.includes('open') && task._isOpen) return true;
                if (activeFilters.includes('completed') && task._isCompleted) return true;
                return false;
            });
        }
        */

        // 3. Advanced Filters
        if (advancedFilters) {
            if (advancedFilters.rows && Array.isArray(advancedFilters.rows) && advancedFilters.rows.length > 0) {
                const { logic, rows } = advancedFilters;
                result = result.filter(task => {
                    const rowMatches = rows.map(row => {
                        const searchVal = String(row.value || '').toLowerCase().trim();
                        if (!searchVal) return true;

                        let valuesToCheck = [];
                        const primaryVal = task[row.field];
                        if (primaryVal !== undefined && primaryVal !== null) {
                            valuesToCheck.push(String(primaryVal).toLowerCase().trim());
                        }

                        // Fallbacks for common fields
                        if (row.field === 'PersonName' || row.field === 'entityName') {
                            if (task.name) valuesToCheck.push(String(task.name).toLowerCase().trim());
                        } else if (row.field === 'Title' || row.field === 'taskTitle') {
                            if (task.Subject) valuesToCheck.push(String(task.Subject).toLowerCase().trim());
                            if (task.Title) valuesToCheck.push(String(task.Title).toLowerCase().trim());
                            if (task.taskTitle) valuesToCheck.push(String(task.taskTitle).toLowerCase().trim());
                        }

                        return valuesToCheck.some(val => {
                            switch (row.operator) {
                                case 'equals': return val === searchVal;
                                case 'not_equal': return val !== searchVal;
                                case 'starts_with': return val.startsWith(searchVal);
                                case 'ends_with': return val.endsWith(searchVal);
                                case 'contains': return val.includes(searchVal);
                                case 'not_contains': return !val.includes(searchVal);
                                default: return val.includes(searchVal);
                            }
                        });
                    });
                    return logic === 'ALL' ? rowMatches.every(m => m) : rowMatches.some(m => m);
                });
            } else if (Object.keys(advancedFilters).length > 0) {
                // Legacy support
                result = result.filter(task => {
                    let matches = true;
                    if (advancedFilters.statusFilter) {
                        const s = advancedFilters.statusFilter.toLowerCase();
                        if (s === 'open' && !task._isOpen) matches = false;
                        else if (s === 'completed' && !task._isCompleted) matches = false;
                        else if (s === 'overdue' && !task._isOverdue) matches = false;
                    }
                    if (matches && advancedFilters.outcomeFilter) {
                        const tOutcome = (task.outcomeName || '').toLowerCase().replace(/\s/g, '');
                        const fOutcome = advancedFilters.outcomeFilter.toLowerCase().replace(/_/g, '');
                        if (!tOutcome.includes(fOutcome)) matches = false;
                    }
                    return matches;
                });
            }
        }

        // 4. Sorting
        if (advancedFilters && advancedFilters.sortBy) {
            result = [...result].sort((a, b) => {
                if (advancedFilters.sortBy === 'date_desc') return (b._parsedDate || 0) - (a._parsedDate || 0);
                if (advancedFilters.sortBy === 'date_asc') return (a._parsedDate || 0) - (b._parsedDate || 0);
                return 0;
            });
        }

        return result;
    }, [tasks, filter, searchTerm, advancedFilters]);

    useEffect(() => {
        const maxPage = Math.ceil(totalRecords / itemsPerPage) || 1;
        if (currentPage > maxPage) {
            setCurrentPage(maxPage);
        }
    }, [totalRecords, currentPage, itemsPerPage]);

    const calendarEvents = useMemo(() => filteredTasks.map(task => ({
        id: task.id,
        title: task.taskTitle,
        date: task._endDateParsed || task._startDateParsed || task._parsedDate,
        status: task._isOverdue ? 'overdue' : (task._isCompleted ? 'completed' : 'open'),
        type: task.type,
        name: task.name,
        phone: task.phone,
        enquiryId: task.enquiryId,
        ownerName: task.mapauDisplayName || 'Mukesh Kumar',
        taskStatus: task.status,
        endDate: task.endDate,
        startDate: task.startDate
    })), [filteredTasks]);

    // Reset selection on search to ensure details panel refreshes with the top result
    useEffect(() => {
        if (searchTerm) {
            setSelectedTask(null);
        }
    }, [searchTerm]);

    // Sync selectedTask with filtered results
    useEffect(() => {
        if (filteredTasks.length > 0) {
            if (!selectedTask || !filteredTasks.some(t => t.id === selectedTask.id)) {
                setSelectedTask(filteredTasks[0]);
            }
        } else {
            setSelectedTask(null);
        }
    }, [filteredTasks]);

    const getPaginationRange = (current, total) => {
        const delta = 2; const range = []; const rangeWithDots = [];
        for (let i = 1; i <= total; i++) { if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) range.push(i); }
        let l;
        for (let i of range) { if (l) { if (i - l === 2) rangeWithDots.push(l + 1); else if (i - l !== 1) rangeWithDots.push('...'); } rangeWithDots.push(i); l = i; }
        return rangeWithDots;
    };

    const totalPages = Math.ceil(totalRecords / itemsPerPage);
    const currentViewData = filteredTasks; // Data is already paginated by the API

    const handlePageChange = (pageNumber) => { if (pageNumber >= 1 && pageNumber <= totalPages) setCurrentPage(pageNumber); };

    const handleRowSelectionChange = useCallback((id, selected) => {
        setRowSelection(prev => {
            const next = { ...prev };
            if (selected) next[id] = true;
            else delete next[id];
            return next;
        });
    }, []);

    const renderAvatar = (name, size = 'sm') => {
        const initials = getInitials(name || 'Unknown');
        const colors = ['#f44336', '#e91e63', '#9c27b0', '#673ab7', '#3f51b5', '#2196f3', '#03a69a', '#4caf50', '#ff9800', '#ff5722'];
        let colorIdx = 0;
        if (initials && initials !== '??' && initials !== 'U') {
            colorIdx = (initials.charCodeAt(0) + (initials.charCodeAt(1) || 0)) % colors.length;
        }
        if (isNaN(colorIdx) || colorIdx < 0) colorIdx = 0;
        const bgColor = colors[colorIdx];

        return (
            <div className={`avatar-initials-circle ${size}`} style={{
                backgroundColor: bgColor,
                position: 'relative'
            }}>
                {initials}
                <div style={{
                    position: 'absolute',
                    top: '-4px',
                    left: '-4px',
                    width: '18px',
                    height: '18px',
                    backgroundColor: '#4a90e2',
                    borderRadius: '50%',
                    color: 'white',
                    fontSize: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px solid white',
                    fontWeight: 'bold',
                    zIndex: 1
                }}>0</div>
            </div>
        );
    };

    const [calendarView, setCalendarView] = useState('month');

    const renderCalendarEvent = (event) => {
        const isCompleted = getIsCompleted(event);
        const overdue = isOverdue(event);
        const isUpcoming = !isCompleted && !overdue;

        let statusType = 'open';
        if (isCompleted) statusType = 'completed';
        else if (overdue) statusType = 'overdue';
        else if (isUpcoming) statusType = 'upcoming';

        const cleanTitle = (event.title || event.taskTitle || 'No Title')?.replace(/#\d+\s*/g, '').trim();
        const isMonthlyView = calendarView === 'month';

        return (
            <div
                key={event.id}
                className={`calendar-card-modern ${statusType} ${isMonthlyView ? 'monthly-compact' : ''}`}
                onClick={(e) => { e.stopPropagation(); handleCalendarEventClick(event); }}
            >
                <div
                    className="calendar-card-title link-style"
                    title={cleanTitle}
                    onClick={(e) => { e.stopPropagation(); handleCalendarEventClick(event); }}
                >
                    {cleanTitle}
                </div>
                <div className="calendar-card-footer" style={{ border: 'none', padding: 0 }}>
                    <div className="calendar-footer-profiles">
                        <div
                            className="calendar-profile-item clickable-lead-link hyperlink"
                            title={`Related: ${event.name}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (event.enquiryId) {
                                    setViewingLeadDetail(event.enquiryId);
                                }
                            }}
                        >
                            <div className="calendar-avatar-mini">{getInitials(event.name)}</div>
                            <span className="calendar-user-name-mini link-style">{event.name}</span>
                        </div>
                        <div className="calendar-profile-item owner" title={`Owner: ${event.ownerName}`}>
                            <div className="calendar-avatar-mini outline-icon">
                                <User size={12} strokeWidth={2.5} />
                            </div>
                            <span className="calendar-user-name-mini">{event.ownerName}</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const handleCalendarEventClick = (event) => {
        const task = tasks.find(t => String(t.id) === String(event.id));
        if (task) {
            setViewingDetailTask(task);
            setDetailMode('view');
        }
    };

    const [isFilterOpen, setIsFilterOpen] = useState(false);

    const handleAddClick = () => handleCreateAction('Add Appointment');

    if (loading && isInitialLoad) {
        return <Loader />;
    }

    return (
        <div className="physical-appointment-container app-fluid-container" style={{ width: '100%', height: 'calc(100vh - 64px)', backgroundColor: '#f8f9fa', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Panels
                isOpen={isCustomDownloadOpen}
                onClose={() => setIsCustomDownloadOpen(false)}
                title="Custom Export"
                width="450px"
            >
                <div className="custom-download-body">
                    <div className="setting-selector">
                        <label>Saved Settings</label>
                        <select className="setting-select" value={selectedCustomSetting} onChange={(e) => {
                            setSelectedCustomSetting(e.target.value);
                            const setting = customSettings.find(s => s.Id.toString() === e.target.value);
                            if (setting) setSelectedExportFields(setting.CsvColumns.split(','));
                        }}>
                            <option value="">Select Setting</option>
                            {customSettings.map(s => <option key={s.Id} value={s.Id}>{s.SettingName}</option>)}
                        </select>
                        <button className="search-setting-btn">🔍</button>
                    </div>
                    <div className="field-search-box">
                        <input type="text" placeholder="Search fields..." className="field-search-input" value={fieldSearchTerm} onChange={(e) => setFieldSearchTerm(e.target.value)} />
                    </div>
                    <div className="fields-list">
                        {customExportFields.filter(f => f.ExposedName.toLowerCase().includes(fieldSearchTerm.toLowerCase())).map((f, i) => (
                            <label key={i} className="field-item">
                                <input type="checkbox" checked={selectedExportFields.includes(f.ExposedName)} onChange={() => {
                                    setSelectedExportFields(prev => prev.includes(f.ExposedName) ? prev.filter(x => x !== f.ExposedName) : [...prev, f.ExposedName]);
                                }} />
                                <span className="field-name">{f.ExposedName}</span>
                            </label>
                        ))}
                    </div>
                    <div className="save-setting-row">
                        <input type="text" placeholder="Save Setting" className="save-setting-input" value={exportSettingName} onChange={(e) => setExportSettingName(e.target.value)} />
                        <button className="save-btn" onClick={handleSaveExportSetting}>Save Setting</button>
                    </div>
                </div>
                <div className="custom-download-footer">
                    <button className="final-download-btn" onClick={handleCustomDownloadSubmit} disabled={isDownloading}>{isDownloading ? 'Processing...' : 'Download'}</button>
                </div>
            </Panels>

            <div className="module-scroll-container" style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', width: '100%' }}>
                <SubHeader
                    pageName="Physical Appointments"
                    pageCode="PAG10035"
                    title="Physical Appointment"
                    searchQuery={searchTerm}
                    setSearchQuery={setSearchTerm}
                    searchPlaceholder="Search physical appointments..."
                    viewMode={viewMode}
                    setViewMode={setViewMode}
                    onAddClick={crmOperationPerms?.Add ? handleAddClick : null}
                    onFilterClick={() => setIsFilterOpen(!isFilterOpen)}
                    showExport={!!crmOperationPerms?.Export}
                    onExportClick={crmOperationPerms?.Export ? handleExportPhysicalAppointment : null}
                    showCustomExport={!!crmOperationPerms?.Export}
                    onCustomExportClick={crmOperationPerms?.Export ? () => setIsCustomDownloadOpen(true) : null}
                    showSettings={true}
                    advanceFilterFields={appointmentFields}
                    onAdvanceFilterSubmit={handleFilterChange}
                    showCustomSearch={!!crmOperationPerms?.Search}
                    onCustomSearchSubmit={crmOperationPerms?.Search ? (id) => { setCustomSearchId(id); setCurrentPage(1); } : null}
                    onCustomSearchClear={handleCustomSearchClear}
                    totalCount={filteredTasks.length}
                    filterCount={customSearchId > 0 ? 1 : 0}
                    activeFilters={filter}
                    massActionPanel={
                        Object.keys(rowSelection).length > 0 ? (
                            <MassOperationPanel
                                selectedCount={Object.keys(rowSelection).length}
                                onClearSelection={() => setRowSelection({})}
                                onAction={(action) => { }}
                                moduleType="appointment"
                            />
                        ) : null
                    }
                />

                <div className="sub-header-controls responsive-header" style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    background: '#fff',
                    padding: '0',
                    minHeight: 0
                }}>
                    <PhysicalAppointmentFilterationActions
                        viewMode={viewMode}
                        onFilterChange={handleFilterChange}
                        onExport={crmOperationPerms?.Export ? handleExportPhysicalAppointment : null}
                        onCustomDownload={crmOperationPerms?.Export ? () => setIsCustomDownloadOpen(true) : null}
                        onActionClick={crmOperationPerms?.Add ? handleCreateAction : null}
                        isOpen={isFilterOpen}
                        setIsOpen={setIsFilterOpen}
                    />
                    <div style={{ width: '100%', overflow: 'visible', position: 'relative', zIndex: 50 }}>
                        <PremiumModuleCards
                            stats={stats}
                            moduleName="Appointment"
                            activeFilters={filter}
                            onStatusClick={(f) => {
                                if (f === 'ResetAll') {
                                    setFilter(['All']);
                                    setSearchTerm('');
                                    setAdvancedFilters(null);
                                    setCustomSearchId(0);
                                    setSelectedCustomSearch('Select Custom Search');
                                    setCurrentPage(1);
                                    return;
                                }
                                setFilter(prev => {
                                    if (f === 'All') return ['All'];
                                    const withoutAll = prev.filter(x => x !== 'All');
                                    if (withoutAll.includes(f)) {
                                        const next = withoutAll.filter(x => f !== 'All' && x !== f);
                                        return next.length === 0 ? ['All'] : next;
                                    }
                                    return [...withoutAll, f];
                                });
                                setCurrentPage(1);
                            }}
                            px="24px"
                        />
                    </div>
                    <div className="content-layout app-fluid-container" style={{ display: 'flex', width: '100%', padding: 0, margin: 0, boxSizing: 'border-box', flex: 1, overflow: 'hidden', minHeight: 0, position: 'relative' }}>
                        {(loading || isInitialLoad) && (
                            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99, background: (tasks.length === 0 || isInitialLoad) ? '#fff' : 'rgba(255, 255, 255, 0.5)', backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Loader message={(tasks.length === 0 || isInitialLoad) ? "Loading physical appointments..." : "Applying Filters..."} />
                            </div>
                        )}
                        <div style={{ display: 'flex', width: '100%', height: '100%', flex: 1, overflow: 'hidden' }}>
                                {viewMode === 'grid' && (
                                    <GridMainPanelLayout
                                        type="physical-appointment"
                                        data={currentViewData}
                                        loading={loading}
                                        totalRecords={totalRecords}
                                        currentPage={currentPage}
                                        totalPages={totalPages}
                                        onPageChange={handlePageChange}
                                        selectedItem={selectedTask}
                                        onSelectItem={setSelectedTask}
                                        rowSelection={rowSelection}
                                        onRowSelectionChange={handleRowSelectionChange}
                                        onViewLead={setViewingLeadDetail}
                                        getCompletionBadge={getTaskCompletionBadge}
                                        formatDate={(d) => d}
                                        getInitials={getInitials}
                                        hideSelection={!hasMassPerms}
                                    />
                                )}

                                {viewMode === 'table' && (
                                    <div className="table-view-container" style={{ padding: 0, overflow: 'hidden', flex: 1, display: 'flex', flexDirection: 'column', width: '100%', minHeight: 0 }}>
                                        <PhysicalAppointmentList
                                            crmOperationPerms={crmOperationPerms}
                                            data={filteredTasks.map(t => maskObject(t))}
                                            searchTerm={searchTerm}
                                            stats={stats}
                                            loading={loading}
                                            currentPage={currentPage}
                                            onPageChange={handlePageChange}
                                            itemsPerPage={itemsPerPage}
                                            onEditAppointment={(task) => { setViewingDetailTask(task); setDetailMode('edit'); }}
                                            onRemoveAppointment={(id) => {
                                                const task = tasks.find(t => t.id === id);
                                                if (!task) return;
                                                setTaskToRemove(task);
                                                setAlertConfig({
                                                    show: true,
                                                    type: 'confirm',
                                                    title: 'Are you sure?',
                                                    message: 'Do you want to remove selected appointment!',
                                                    onConfirm: () => {
                                                        setAlertConfig(prev => ({ ...prev, show: false }));
                                                        handleRemove(task);
                                                    },
                                                    onCancel: () => setAlertConfig(prev => ({ ...prev, show: false }))
                                                });
                                            }}
                                            onViewAppointment={(task, mode = 'view') => { setViewingDetailTask(task); setDetailMode(mode); }}
                                            onMarkCompleted={(task) => { setMarkCompleteTask(task); setMarkCompletePayload({ OutcomesId: '', Remarks: '' }); setShowMarkCompleteModal(true); }}
                                            onViewLead={(id) => setViewingLeadDetail(id)}
                                            onRefresh={fetchAppointments}
                                            onAddComment={handleAddCommentClick}
                                        />
                                    </div>
                                )}

                                {viewMode === 'calendar' && (
                                    <div className="calendar-view-wrapper" style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
                                        {(loading || isInitialLoad) && <Loader message="Syncing calendar..." />}
                                        <DynamicCalendar
                                            headerLegend={
                                                <Stack direction="row" spacing={2.5} sx={{ mt: 1, flexWrap: 'wrap', gap: '12px' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#2563eb' }} />
                                                        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>Open</Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#f97316' }} />
                                                        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>Upcoming</Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ef4444' }} />
                                                        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>Overdue</Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#10b981' }} />
                                                        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>Completed</Typography>
                                                    </Box>
                                                </Stack>
                                            }
                                            onEventDrop={(info) => console.log('Event Dropped:', info)}
                                            events={filteredTasks.map(t => {
                                                const maskedTask = maskObject(t);
                                                return {
                                                    ...maskedTask,
                                                    id: maskedTask.id || maskedTask.PhysicalAppointmentId,
                                                    title: maskedTask.taskTitle || maskedTask.Title || 'Physical Appointment',
                                                    start: maskedTask.startDate || maskedTask.date,
                                                    end: maskedTask.endDate || maskedTask.startDate || maskedTask.date,
                                                    extendedProps: { ...maskedTask }
                                                };
                                            })}
                                            eventRenderer={renderCalendarEvent}
                                            onEventClick={handleCalendarEventClick}
                                            onDateChange={(date, view) => setCalendarView(view)}
                                            initialView="month"
                                            initialDate={new Date()}
                                            entityLabel="Physical Appointment"
                                        />
                                    </div>
                                )}
                            </div>
                    </div>

                    {detailMode === 'view' ? (
                        <AppointmentTaskDetailPanel
                            isOpen={!!viewingDetailTask}
                            onClose={() => setViewingDetailTask(null)}
                            id={viewingDetailTask?.id}
                            type="physical-appointment"
                            userId={localStorage.getItem('userId') || "34594"}
                            onViewLead={(id) => setViewingLeadDetail(id)}
                            detailData={viewingDetailTask}
                        />
                    ) : (
                        <Panels
                            isOpen={!!viewingDetailTask}
                            onClose={() => setViewingDetailTask(null)}
                            title={detailMode === 'edit' ? 'Edit Physical Appointment' : 'Physical Appointment Details'}
                            width="50%"
                        >
                            <PhysicalAppointmentDetails
                                mode={detailMode}
                                task={viewingDetailTask}
                                onBack={() => { setViewingDetailTask(null); fetchAppointments(); }}
                                leadId={viewingDetailTask?.EntityId || viewingDetailTask?.leadId}
                                isInline={true}
                            />
                        </Panels>
                    )}

                    {viewingLeadDetail && (
                        <CommonLeadDetails
                            leadId={viewingLeadDetail}
                            onBack={() => setViewingLeadDetail(null)}
                            module="physical_appointment"
                        />
                    )}
                </div>
            </div>

            <Dialog
                open={showMarkCompleteModal}
                onClose={() => setShowMarkCompleteModal(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: '12px' }
                }}
            >
                <DialogTitle sx={{ borderBottom: '1px solid #f1f5f9', pb: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6" fontWeight="600" color="#334155">Add Outcome</Typography>
                        <Box onClick={() => setShowMarkCompleteModal(false)} sx={{ cursor: 'pointer', color: '#64748b', '&:hover': { color: '#334155' } }}>&times;</Box>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
                        <FormControl fullWidth>
                            <InputLabel sx={{ color: '#64748b', mb: 1, position: 'relative', transform: 'none', fontSize: '14px', fontWeight: '500' }}>Outcome</InputLabel>
                            <Select
                                value={markCompletePayload.OutcomesId}
                                onChange={(e) => setMarkCompletePayload({ ...markCompletePayload, OutcomesId: e.target.value })}
                                displayEmpty
                                sx={{ borderRadius: '8px', backgroundColor: '#fff', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' } }}
                            >
                                <MenuItem value="" disabled>Select Outcome</MenuItem>
                                <MenuItem value="1425">Visit done</MenuItem>
                                <MenuItem value="1426">revisit done</MenuItem>
                            </Select>
                        </FormControl>

                        <Box>
                            <Typography component="label" sx={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#64748b', mb: 1 }}>Remarks*</Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                value={markCompletePayload.Remarks}
                                onChange={(e) => setMarkCompletePayload({ ...markCompletePayload, Remarks: e.target.value })}
                                placeholder="Enter Remarks*"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '8px',
                                        backgroundColor: '#fff',
                                        '& fieldset': { borderColor: '#e2e8f0' }
                                    }
                                }}
                            />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3, borderTop: '1px solid #f1f5f9', gap: 1 }}>
                    <Button
                        onClick={() => setShowMarkCompleteModal(false)}
                        sx={{
                            borderRadius: '8px', px: 3, textTransform: 'none', fontWeight: '600',
                            color: '#fff', backgroundColor: '#14b8a6', '&:hover': { backgroundColor: '#0f766e' }
                        }}
                    >
                        Close
                    </Button>
                    <Button
                        variant="contained"
                        onClick={submitMarkComplete}
                        disabled={loading}
                        sx={{
                            borderRadius: '8px', px: 3, textTransform: 'none', fontWeight: '600',
                            backgroundColor: '#22c55e', '&:hover': { backgroundColor: '#16a34a' }
                        }}
                    >
                        {loading ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Alert {...alertConfig} />

            {/* Add Comment Modal */}
            <Dialog
                open={showCommentModal}
                onClose={() => setShowCommentModal(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: '12px', padding: '10px' }
                }}
            >
                <DialogTitle sx={{
                    m: 0,
                    p: 2,
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: '#333',
                    borderBottom: '1px solid #eee',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    Add Comment
                    <IconButton
                        aria-label="close"
                        onClick={() => setShowCommentModal(false)}
                        sx={{ color: (theme) => theme.palette.grey[500] }}
                    >
                        <LucideX size={20} />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 3, pt: 4 }}>
                    <Box sx={{ mb: 3 }}>
                        <Typography sx={{ mb: 1, fontWeight: 600, color: '#444', fontSize: '14px' }}>
                            Comment
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            placeholder="Please Enter Comment"
                            value={commentData.remarks}
                            onChange={(e) => setCommentData({ ...commentData, remarks: e.target.value })}
                            variant="outlined"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: '#fff',
                                    borderRadius: '8px'
                                }
                            }}
                        />
                    </Box>

                    <Box sx={{ mb: 3 }}>
                        <Typography sx={{ mb: 1, fontWeight: 600, color: '#444', fontSize: '14px' }}>
                            Attachment Title
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder="Enter Attachment Title"
                            value={commentData.attachmentTitle}
                            onChange={(e) => setCommentData({ ...commentData, attachmentTitle: e.target.value })}
                            variant="outlined"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: '#fff',
                                    borderRadius: '8px'
                                }
                            }}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Button
                            variant="contained"
                            component="label"
                            sx={{
                                backgroundColor: '#28a745',
                                '&:hover': { backgroundColor: '#218838' },
                                textTransform: 'none',
                                borderRadius: '4px',
                                px: 3
                            }}
                        >
                            Browse...
                            <input
                                type="file"
                                hidden
                                onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                        setCommentData({ ...commentData, attachmentFile: file, attachmentName: file.name });
                                    }
                                }}
                            />
                        </Button>
                        <Typography sx={{ color: '#666', fontSize: '14px' }}>
                            {commentData.attachmentName || 'No file chosen'}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3, gap: 1 }}>
                    <Button
                        onClick={() => setShowCommentModal(false)}
                        sx={{
                            color: '#6c757d',
                            textTransform: 'none',
                            fontWeight: 'bold',
                            border: '1px solid #dee2e6',
                            '&:hover': { backgroundColor: '#f8f9fa' }
                        }}
                    >
                        Close
                    </Button>
                    <Button
                        onClick={submitAddComment}
                        variant="contained"
                        sx={{
                            backgroundColor: '#28a745',
                            '&:hover': { backgroundColor: '#218838' },
                            textTransform: 'none',
                            fontWeight: 'bold',
                            px: 4
                        }}
                        disabled={loading}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default PhysicalAppointmentModule;
